/**
 * Live table projection of the canonical telemetry store (spec
 * `vehicle-realtime-data-consistency`, task 3.9 / design step 10).
 *
 * `LiveTrackPoints` used to merge the route response and the SignalR cache on its
 * own: route rows were flattened into redux, every incoming realtime packet was
 * folded into that list, and a missing speed was forward-filled from an older
 * packet so the first row always looked like it carried the current speed. That
 * independent merge is how the live table could disagree with the sidebar
 * (#119201) and how a newest non-speed packet was made to "carry" an old speed
 * (bugfix.md 1.8).
 *
 * Here the rows come from `getTableDataByPlateNumber` - real packets, already
 * deduped, plate-scoped and sorted newest-first by the reducer - and this module
 * only projects them into the flat shape `TrackPointsTableWrapper` renders.
 *
 * Two rules are load-bearing:
 *
 * - No row is ever synthesized and no value is filled in from another packet. An
 *   aggregate built from latest-vehicles fields, or a forward-filled speed, would
 *   mix observations made at different times (design 3.5).
 * - The canonical movement is matched against the newest **speed-bearing** row
 *   (`movementObservedAt != null`), not against the first row: the newest packet
 *   may legitimately carry no speed (revised consistency contract, bugfix.md 2.8).
 */
import { flattenObject } from "../../utils";

/** Stable empty result so an unknown plate keeps a stable array reference. */
const EMPTY_ROWS = [];

/**
 * Canonical-only keys added by `normalizePacket`. They are stripped before
 * flattening so a rendered row keeps exactly the raw packet keys the column
 * configuration refers to (`source_time`, `velocity.speed`, ...).
 */
const CANONICAL_KEYS = [
  "plateNumber",
  "sourceId",
  "packetId",
  "packetType",
  "sourceTime",
  "serverTime",
  "source",
  "latestPacketAt",
  "movementObservedAt",
  "fields",
];

const toRawPacket = (row) => {
  const raw = { ...row };
  CANONICAL_KEYS.forEach((key) => delete raw[key]);
  return raw;
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

/**
 * Newest speed-bearing row of the live table. Rows arrive newest-first, so the
 * first row carrying a speed observation is the newest one.
 *
 * @param {Array<Object>} rows - Canonical rows from `getTableDataByPlateNumber`.
 * @returns {Object|null} The row, or `null` when no loaded row carries a speed.
 */
export const findNewestSpeedRow = (rows = []) =>
  (Array.isArray(rows) ? rows : []).find((row) => row?.movementObservedAt != null) ?? null;

/**
 * Live summary of the selected vehicle, and the table row that backs it.
 *
 * The summary speed is always the canonical `movement.speed` - never recomputed
 * from the rows - so the sidebar and this summary cannot diverge. `matchesNewestSpeedRow`
 * reports whether the loaded route window actually contains that observation; the
 * match is only required then (design "Revised Consistency Contract"). When the
 * canonical observation is outside the loaded window the summary still shows the
 * canonical value and no row is fabricated to back it.
 *
 * @param {Object} args
 * @param {Object|null} [args.movement] Canonical movement of the plate.
 * @param {Array<Object>} [args.rows] Canonical rows of the plate.
 * @returns {{ speed: number|null, observedAt: number|null,
 *   newestSpeedRow: Object|null, matchesNewestSpeedRow: boolean }}
 */
export const resolveLiveSummary = ({ movement = null, rows = [] } = {}) => {
  const newestSpeedRow = findNewestSpeedRow(rows);
  const observedAt = toFiniteNumber(movement?.observedAt);
  const rowObservedAt = toFiniteNumber(newestSpeedRow?.movementObservedAt);

  return {
    speed: movement ? (movement.speed ?? null) : null,
    observedAt,
    newestSpeedRow,
    matchesNewestSpeedRow:
      observedAt !== null && rowObservedAt !== null && rowObservedAt === observedAt,
  };
};

/**
 * Projects canonical rows into the flat rows the track points table renders.
 *
 * Ordering, dedup and the 200 row cap are already applied by the reducer, so rows
 * are only flattened here (newest-first preserved). Each row keeps its own
 * `movementObservedAt`, and the row that backs the canonical movement is marked
 * with `is_current_movement` - that marker replaces the old, invalid
 * "row 0 carries the current speed" assumption.
 *
 * @param {Array<Object>} rows - Canonical rows from `getTableDataByPlateNumber`.
 * @param {Object} [options]
 * @param {Object|null} [options.movement] Canonical movement of the plate.
 * @returns {Array<Object>} Flattened rows, newest-first. Never a fabricated row.
 */
export const buildLiveTableRows = (rows = [], { movement = null } = {}) => {
  if (!Array.isArray(rows) || rows.length === 0) return EMPTY_ROWS;

  const { newestSpeedRow, matchesNewestSpeedRow } = resolveLiveSummary({ movement, rows });
  const currentMovementRow = matchesNewestSpeedRow ? newestSpeedRow : null;

  return rows.map((row) => {
    const flatRow = flattenObject(toRawPacket(row));

    // Kept so consumers can find the newest speed-bearing row after flattening.
    flatRow.movementObservedAt = row?.movementObservedAt ?? null;
    flatRow.is_current_movement = row === currentMovementRow;

    return flatRow;
  });
};
